"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggablePieChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_charts_1 = require("@gooddata/sdk-ui-charts");
var sdk_model_1 = require("@gooddata/sdk-model");
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var PieChartConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/PieChartConfigurationPanel"));
var bucket_1 = require("../../../constants/bucket");
var properties_1 = require("../../../constants/properties");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var SortConfig_1 = require("../../../interfaces/SortConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var pieChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/pieChartUiConfigHelper");
/**
 * PluggablePieChart
 *
 * ## Buckets
 *
 * | Name     | Id       | Accepts             |
 * |----------|----------|---------------------|
 * | Measures | measures | measures only       |
 * | ViewBy   | view     | attribute or date   |
 *
 * ### Bucket axioms
 *
 * - |ViewBy| ≤ 1
 * - |Measures| ≥ 1 ∧ ≤ 20
 * - |ViewBy| = 1 ⇒ |Measures| = 1
 * - |ViewBy| = 0 ⇒ |Measures| ≥ 1
 *
 * ## Dimensions
 *
 * The PluggablePieChart always creates two dimensional execution.
 *
 * With measures only:
 * - [[], [MeasureGroupIdentifier]]
 * With viewBy:
 * - [[MeasureGroupIdentifier], [ViewBy]]
 *
 * ## Default sorts
 *
 * When Pie Chart is used with measures only, it's sorted by their order by default.
 * When Pie Chart chart is used with viewBy attribute or date, it's sorted by the values of the measure by default.
 *
 * Default sort behavior can be overriden by sortBy option.
 *
 */
var PluggablePieChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggablePieChart, _super);
    function PluggablePieChart(props) {
        var _this = _super.call(this, props) || this;
        _this.type = sdk_ui_1.VisualizationTypes.PIE;
        _this.supportedPropertiesList = supportedProperties_1.PIECHART_SUPPORTED_PROPERTIES;
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggablePieChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var _a;
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: cloneDeep_1.default(uiConfig_1.DEFAULT_PIE_UICONFIG) });
        newReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(newReferencePoint);
        newReferencePoint = bucketHelper_1.removeAllDerivedMeasures(newReferencePoint);
        var buckets = (_a = clonedReferencePoint === null || clonedReferencePoint === void 0 ? void 0 : clonedReferencePoint.buckets) !== null && _a !== void 0 ? _a : [];
        var attributes = bucketHelper_1.getAttributeItems(buckets);
        if (attributes.length) {
            var limitedBuckets = bucketHelper_1.limitNumberOfMeasuresInBuckets(buckets, 1);
            var limitedMeasures = bucketHelper_1.getMeasureItems(limitedBuckets);
            set_1.default(newReferencePoint, bucket_1.BUCKETS, [
                {
                    localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                    items: limitedMeasures,
                },
                {
                    localIdentifier: sdk_ui_1.BucketNames.VIEW,
                    items: attributes.slice(0, 1),
                },
            ]);
        }
        else {
            var measures = bucketHelper_1.getMeasureItems(buckets);
            if (measures.length > 1) {
                set_1.default(newReferencePoint, uiConfig_1.UICONFIG, cloneDeep_1.default(uiConfig_1.PIE_UICONFIG_WITH_MULTIPLE_METRICS));
            }
            else {
                set_1.default(newReferencePoint, uiConfig_1.UICONFIG, cloneDeep_1.default(uiConfig_1.PIE_UICONFIG_WITH_ONE_METRIC));
            }
            set_1.default(newReferencePoint, bucket_1.BUCKETS, [
                {
                    localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                    items: measures,
                },
                {
                    localIdentifier: sdk_ui_1.BucketNames.VIEW,
                    items: [],
                },
            ]);
        }
        newReferencePoint = pieChartUiConfigHelper_1.setPieChartUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, false);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        if (!this.featureFlags.enableChartsSorting) {
            newReferencePoint = sort_1.removeSort(newReferencePoint);
        }
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggablePieChart.prototype.getDefaultAndAvailableSort = function (measures, viewBy) {
        if (!isEmpty_1.default(measures) && !isEmpty_1.default(viewBy)) {
            return {
                defaultSort: [sdk_model_1.newMeasureSort(measures[0].localIdentifier, "desc")],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier, [measures[0].localIdentifier], true, false),
                ],
            };
        }
        return {
            defaultSort: [],
            availableSorts: [],
        };
    };
    PluggablePieChart.prototype.isSortDisabled = function (referencePoint, availableSorts) {
        var buckets = referencePoint.buckets;
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var disabled = viewBy.length < 1 || measures.length < 1 || availableSorts.length === 0;
        var disabledExplanation = sort_1.getCustomSortDisabledExplanation(measures, viewBy, this.intl);
        return {
            disabled: disabled,
            disabledExplanation: disabledExplanation,
        };
    };
    PluggablePieChart.prototype.getSortConfig = function (referencePoint) {
        var buckets = referencePoint.buckets, properties = referencePoint.properties, previousAvailableSorts = referencePoint.availableSorts;
        var measures = bucketHelper_1.getMeasureItems(buckets);
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var _a = this.getDefaultAndAvailableSort(measures, viewBy), defaultSort = _a.defaultSort, availableSorts = _a.availableSorts;
        var _b = this.isSortDisabled(referencePoint, availableSorts), disabled = _b.disabled, disabledExplanation = _b.disabledExplanation;
        return Promise.resolve(tslib_1.__assign({ supported: true, disabled: disabled, appliedSort: _super.prototype.reuseCurrentSort.call(this, previousAvailableSorts, properties, availableSorts, defaultSort), defaultSort: defaultSort,
            availableSorts: availableSorts }, (disabledExplanation && { disabledExplanation: disabledExplanation })));
    };
    PluggablePieChart.prototype.renderConfigurationPanel = function (insight) {
        var configPanelElement = this.getConfigPanelElement();
        if (configPanelElement) {
            this.renderFun(react_1.default.createElement(PieChartConfigurationPanel_1.default, { locale: this.locale, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, pushData: this.handlePushData, colors: this.colors, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags, references: this.references }), configPanelElement);
        }
    };
    PluggablePieChart.prototype.buildVisualizationConfig = function (options, supportedControls) {
        var baseVisualizationConfig = _super.prototype.buildVisualizationConfig.call(this, options, supportedControls);
        if (this.environment === properties_1.DASHBOARDS_ENVIRONMENT) {
            return tslib_1.__assign(tslib_1.__assign({}, baseVisualizationConfig), { chart: {
                    verticalAlign: sdk_ui_charts_1.TOP,
                } });
        }
        return baseVisualizationConfig;
    };
    return PluggablePieChart;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggablePieChart = PluggablePieChart;
//# sourceMappingURL=PluggablePieChart.js.map